/**
 * components/ui.tsx
 * Layout + text primitives shared by every screen — the RN equivalents of the
 * design's `.t-row / .t-col / .t-stack / .t-cluster / .t-spread / .t-grow`
 * helpers, plus a single `Txt` so typography (IBM Plex, color, size) is applied
 * once, never re-declared per call site.
 */
import React from 'react';
import { View, Text, StyleProp, ViewStyle, TextStyle } from 'react-native';
import { LightColors as C, FontFamily as FF, FontSize as FS, FontWeight as FW } from '../theme/theme';

type V = StyleProp<ViewStyle>;

interface BoxProps {
  children?: React.ReactNode;
  style?: V;
  gap?: number;
}

/** Horizontal flex row, centered cross-axis (gap default 8). */
export const Row: React.FC<BoxProps> = ({ children, style, gap = 8 }) => (
  <View style={[{ flexDirection: 'row', alignItems: 'center', gap }, style]}>{children}</View>
);

/** Vertical flex column (gap default 8). */
export const Col: React.FC<BoxProps> = ({ children, style, gap = 8 }) => (
  <View style={[{ flexDirection: 'column', gap }, style]}>{children}</View>
);

/** Vertical stack with the design's default 12px gap. */
export const Stack: React.FC<BoxProps> = ({ children, style, gap = 12 }) => (
  <View style={[{ flexDirection: 'column', gap }, style]}>{children}</View>
);

/** Wrapping, center-aligned cluster (chips, tags). */
export const Cluster: React.FC<BoxProps> = ({ children, style, gap = 8 }) => (
  <View style={[{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap }, style]}>{children}</View>
);

/** Space-between row (title ↔ actions). */
export const Spread: React.FC<BoxProps> = ({ children, style, gap = 12 }) => (
  <View style={[{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap }, style]}>
    {children}
  </View>
);

/** Hairline divider. */
export const Divider: React.FC<{ style?: V }> = ({ style }) => (
  <View style={[{ height: 1, backgroundColor: C.line }, style]} />
);

export interface TxtProps {
  children?: React.ReactNode;
  size?: number;
  weight?: TextStyle['fontWeight'];
  color?: string;
  mono?: boolean;
  align?: TextStyle['textAlign'];
  transform?: TextStyle['textTransform'];
  tracking?: number;
  lineHeight?: number;
  italic?: boolean;
  numberOfLines?: number;
  style?: StyleProp<TextStyle>;
}

/** The single text primitive — applies font family, sizing and color once. */
export const Txt: React.FC<TxtProps> = ({
  children,
  size = FS.md,
  weight = FW.regular,
  color = C.ink,
  mono = false,
  align,
  transform,
  tracking,
  lineHeight,
  italic,
  numberOfLines,
  style,
}) => (
  <Text
    numberOfLines={numberOfLines}
    style={[
      {
        fontFamily: mono ? FF.mono : FF.sans,
        fontSize: size,
        fontWeight: weight,
        color,
        textAlign: align,
        textTransform: transform,
        letterSpacing: tracking,
        lineHeight,
        fontStyle: italic ? 'italic' : 'normal',
      },
      style,
    ]}
  >
    {children}
  </Text>
);

/** Small uppercase section label (used across cards/sidebar/perf). */
export const Eyebrow: React.FC<{ children: React.ReactNode; color?: string; style?: StyleProp<TextStyle> }> = ({
  children,
  color = C.ink4,
  style,
}) => (
  <Txt size={11} weight={FW.semibold} color={color} transform="uppercase" tracking={0.7} style={style}>
    {children}
  </Txt>
);
